import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Pagination from '@/components/feature/Pagination';
import WorkCard from '@/components/feature/WorkCard';
import type { Work } from '@/mocks/catalog';
import { categoryMeta, genreOptions, regionOptions, sortOptions, typeLabel, yearOptions, type MediaType } from '@/mocks/catalog';
import FilterPanel, { applyFilters, type CatalogQuery } from '@/pages/category/components/FilterPanel';

interface CatalogBrowserProps {
  type: MediaType;
  list: Work[];
  pageSize?: number;
  headingTitle?: string;
}

const parseQuery = (params: URLSearchParams): CatalogQuery => {
  const pick = (key: string, options: string[], fallback: string) => {
    const raw = params.get(key) || params.get('filter');
    return raw && options.includes(raw) ? raw : fallback;
  };
  return {
    genre: pick('genre', genreOptions, '全部'),
    region: pick('region', regionOptions, '全部'),
    year: pick('year', yearOptions, '全部'),
    sort: pick('sort', [...sortOptions], '最新上线'),
  };
};

export default function CatalogBrowser({ type, list, pageSize = 12, headingTitle }: CatalogBrowserProps) {
  const [params, setParams] = useSearchParams();
  const meta = categoryMeta[type];
  const query = useMemo(() => parseQuery(params), [params]);
  const requestedPage = Math.max(1, Number(params.get('page') || '1') || 1);

  const filtered = useMemo(() => applyFilters(list, query), [list, query]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(requestedPage, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const updateQuery = (next: CatalogQuery) => {
    const search = new URLSearchParams();
    if (next.genre !== '全部') search.set('genre', next.genre);
    if (next.region !== '全部') search.set('region', next.region);
    if (next.year !== '全部') search.set('year', next.year);
    if (next.sort !== '最新上线') search.set('sort', next.sort);
    setParams(search, { replace: true });
  };

  const reset = () => setParams(new URLSearchParams(), { replace: true });

  const activeChips = [
    query.genre !== '全部' ? { key: 'genre', label: query.genre } : null,
    query.region !== '全部' ? { key: 'region', label: query.region } : null,
    query.year !== '全部' ? { key: 'year', label: `${query.year}年` } : null,
  ].filter((chip): chip is { key: string; label: string } => chip !== null);

  return (
    <div className="w-full">
      <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-accent-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <Link to={`/category/${type}`} className="transition-colors hover:text-accent-700">
            {meta.en}
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-800">{headingTitle || meta.name}</span>
        </nav>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-xl font-bold text-foreground-950 md:text-2xl">
              {headingTitle || meta.name}
            </h1>
            <p className="mt-1.5 text-sm text-foreground-500">{meta.desc}</p>
          </div>
          <p className="font-label text-xs text-foreground-500">
            共 <span className="font-bold text-foreground-950">{filtered.length}</span> 部 · 第 {currentPage} / {totalPages} 页
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-start">
        <FilterPanel resultCount={filtered.length} value={query} onChange={updateQuery} onReset={reset} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-label text-xs text-foreground-500">当前筛选</span>
            {activeChips.length === 0 ? (
              <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">全部内容</span>
            ) : (
              activeChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => updateQuery({ ...query, [chip.key]: '全部' })}
                  className="flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-1 text-xs text-accent-900 transition-colors hover:bg-accent-200"
                >
                  {chip.label}
                  <i className="ri-close-line"></i>
                </button>
              ))
            )}
            <button
              type="button"
              onClick={() =>
                updateQuery({
                  ...query,
                  sort: query.sort === '最新上线' ? '评分最高' : query.sort === '评分最高' ? '最多关注' : '最新上线',
                })
              }
              className="ml-auto flex items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:bg-accent-100 hover:text-accent-800"
            >
              <i className="ri-sort-desc text-accent-600"></i>
              {query.sort}
            </button>
          </div>

          {filtered.length === 0 ? (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-line text-xl"></i>
              </span>
              <p className="mt-4 text-base font-semibold text-foreground-950">没有找到符合条件的影视内容</p>
              <p className="mt-1.5 max-w-sm text-sm text-foreground-500">
                换个类型或年份试试，也可以直接重置筛选条件，浏览{meta.name}的全部内容。
              </p>
              <button
                type="button"
                onClick={reset}
                className="mt-5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                重置筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 xl:grid-cols-4">
                {pageItems.map((work) => (
                  <WorkCard key={work.id} work={work} showPlayCount />
                ))}
              </div>
              <Pagination current={currentPage} total={totalPages} />
            </>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200/70 bg-background-100 px-4 py-3">
            <p className="text-xs text-foreground-500">
              <i className="ri-information-line mr-1 text-accent-600"></i>
              播放相关信息以对应平台说明为准，本站不提供播放与下载入口。
            </p>
            <Link to="/" className="whitespace-nowrap text-xs text-foreground-600 hover:text-accent-700">
              返回首页
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(Object.keys(categoryMeta) as MediaType[])
          .filter((key) => key !== type)
          .map((key) => (
            <Link
              key={key}
              to={`/category/${key}`}
              className="group flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-accent-300"
            >
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={categoryMeta[key].cover}
                  alt={`${categoryMeta[key].name}封面`}
                  title={`${categoryMeta[key].name} - 91大神`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-950 group-hover:text-accent-700">
                  前往{categoryMeta[key].name}
                </p>
                <p className="truncate text-xs text-foreground-500">{typeLabel[key]}资源 · {categoryMeta[key].desc}</p>
              </div>
              <i className="ri-arrow-right-line ml-auto text-foreground-400"></i>
            </Link>
          ))}
      </div>
    </div>
  );
}